import { useState, useEffect, useCallback } from 'react';
import { initPOV, togglePOV, getPOV } from '../lib/pricingPOV';
import {
  initOddsFormat,
  cycleOddsFormat,
  getOddsFormat,
  setOddsFormat as setOddsFormatGlobal,
} from '../lib/oddsConversion';
import { initDataMode, toggleDataMode, getDataMode, type DataMode } from '../lib/dataMode';
import {
  initOrderSort,
  getOrderSort,
  setOrderSort as setOrderSortGlobal,
  type OrderSortPreference,
} from '../lib/orderSortPreference';
import type { PricingPOV, OddsFormat } from '../types';

export interface DisplayPreferences {
  pov: PricingPOV;
  oddsFormat: OddsFormat;
  dataMode: DataMode;
  chartPeriod: '1m' | '5m' | '15m' | '1h' | '1d';
  orderSort: OrderSortPreference;
  handlePOVToggle: () => void;
  handleOddsFormatToggle: () => void;
  handleOddsFormatSet: (format: OddsFormat) => void;
  handleDataModeToggle: () => void;
  handleChartPeriodChange: (period: '1m' | '5m' | '15m' | '1h' | '1d') => void;
  handleOrderSortChange: (pref: OrderSortPreference) => void;
}

export function useDisplayPreferences(): DisplayPreferences {
  const [pov, setPov] = useState<PricingPOV>(getPOV());
  const [oddsFormat, setOddsFormat] = useState<OddsFormat>(getOddsFormat());
  const [dataMode, setDataModeState] = useState<DataMode>(getDataMode());
  const [chartPeriod, setChartPeriod] = useState<'1m' | '5m' | '15m' | '1h' | '1d'>('5m');
  const [orderSort, setOrderSortState] = useState<OrderSortPreference>(getOrderSort());

  // Initialize on mount
  useEffect(() => {
    initPOV();
    initOddsFormat();
    initDataMode();
    initOrderSort();
  }, []);

  const handlePOVToggle = useCallback(() => {
    togglePOV();
    setPov(getPOV());
  }, []);

  const handleOddsFormatToggle = useCallback(() => {
    cycleOddsFormat();
    setOddsFormat(getOddsFormat());
  }, []);

  const handleOddsFormatSet = useCallback((format: OddsFormat) => {
    setOddsFormatGlobal(format);
    setOddsFormat(format);
  }, []);

  const handleDataModeToggle = useCallback(() => {
    toggleDataMode();
    setDataModeState(getDataMode());
  }, []);

  const handleChartPeriodChange = useCallback((period: '1m' | '5m' | '15m' | '1h' | '1d') => {
    setChartPeriod(period);
  }, []);

  const handleOrderSortChange = useCallback((pref: OrderSortPreference) => {
    setOrderSortGlobal(pref);
    setOrderSortState(pref);
  }, []);

  return {
    pov,
    oddsFormat,
    dataMode,
    chartPeriod,
    orderSort,
    handlePOVToggle,
    handleOddsFormatToggle,
    handleOddsFormatSet,
    handleDataModeToggle,
    handleChartPeriodChange,
    handleOrderSortChange,
  };
}
